import React from 'react';
import Highlight from 'prism-react-renderer';
import Prism from 'prismjs';
import classnames from 'classnames';
import range from 'lodash/range';

import style from './CodeSnippetSlice.sass';

export interface Props {
  code: string;
  language: string;
  file?: string;
  highlight?: string;
  className?: string;
}

const normalizeClassName = (className: string) =>
  className
    .split(/\s+/)
    .map((className) => {
      const normalized = className.replace(/-([a-z])/gi, (match, letter) => letter.toUpperCase());
      return (style as any)[normalized];
      // if (!style[normalized]) console.log(className);
      // return classnames(style[normalized], className);
    })
    .filter((className) => className);

interface HighlightedIndex extends Record<number, true> {}

const getHighlightedIndex = (highlight: string | undefined) => {
  if (!highlight) return {};

  return highlight.split(',').reduce((highlights, item) => {
    item = item.trim();

    const match = item.match(/(\d+)-(\d+)/);

    if (match && match.length === 3) {
      const start = parseInt(match[1]);
      const end = parseInt(match[2]);

      if (isNaN(start) || isNaN(end) || Math.abs(start - end) > 50) return highlights;

      range(start, end + 1, 1).forEach((index) => {
        highlights[index] = true;
      });

      return highlights;
    }

    const value = parseInt(item);

    if (isNaN(value)) highlights;

    highlights[value] = true;

    return highlights;
  }, {} as HighlightedIndex);
};

const CodeSnippetSlice: React.FC<Props> = ({ code, language, file, highlight, className: rootClassName }) => {
  const highlights = getHighlightedIndex(highlight);

  return (
    <Highlight Prism={Prism as any} code={code} language={language as any}>
      {({ className, tokens, getLineProps, getTokenProps }) => (
        <div className={classnames(style.root, rootClassName)}>
          {!!file && <div className={style.file}>{file}</div>}

          <div className={style.container}>
            <pre className={classnames(className, style.pre)}>
              <table className={classnames(className, style.code)}>
                <tbody>
                  {tokens.map((line, index) => {
                    const lineProps = getLineProps({ line, key: index });
                    const numbered = !!file;
                    const highlighted = !!highlights[index + 1];

                    return (
                      <tr
                        key={index}
                        {...lineProps}
                        className={classnames(normalizeClassName(lineProps.className), {
                          [style.highlighted]: highlighted
                        })}
                        style={undefined}
                      >
                        {numbered && <td className={style.lineNumber}>{index + 1}</td>}
                        <td className={style.lineContent}>
                          {line.map((token, key) => {
                            const tokenProps = getTokenProps({ token, key });

                            if (token.empty) return <br key={key} />;

                            return (
                              <span
                                key={key}
                                {...tokenProps}
                                className={classnames(normalizeClassName(tokenProps.className))}
                                style={undefined}
                              />
                            );
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </pre>
          </div>
        </div>
      )}
    </Highlight>
  );
};

export default CodeSnippetSlice;
